<template>
  <section class="mx-auto flex w-full max-w-[980px] flex-col gap-4 px-4 pb-8 pt-3 sm:px-6 lg:px-8">
    <div class="grid w-full items-start justify-center gap-5 xl:grid-cols-[minmax(0,560px)_360px]">
      <div class="w-full self-start overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.10)] sm:sticky sm:top-24" :class="{ 'review-detail-card': isBusinessPost }">
        <div class="flex flex-wrap items-center gap-3 px-4 py-3 sm:flex-nowrap sm:px-5 sm:py-4">
          <button
            type="button"
            class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
            aria-label="Back"
            @click="goBack"
          >
            <i class="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
          </button>

          <router-link v-if="!isBusinessPost" class="flex min-w-0 flex-1 items-center gap-3" :to="postedByRoute">
            <img
              v-if="flyer.authorAvatar"
              class="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
              :src="flyer.authorAvatar"
              :alt="flyer.author || 'Author'"
              loading="lazy"
            />
            <div v-else class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
              {{ authorInitials }}
            </div>
            <span class="min-w-0">
              <strong class="block truncate text-sm font-semibold text-slate-950">{{ flyer.author || 'Unknown author' }}</strong>
              <span class="block truncate text-xs text-slate-500">
                <span v-if="postedByUsername">{{ postedByUsername }}</span>
                <span v-if="postedByUsername && formattedDate"> · </span>
                <span>{{ formattedDate }}</span>
              </span>
            </span>
          </router-link>

          <div class="relative shrink-0" :class="{ 'ml-auto': isBusinessPost }">
            <button
              type="button"
              class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              :aria-label="`${contentTypeName} actions`"
              :aria-expanded="detailActionsOpen"
              @click="detailActionsOpen = !detailActionsOpen"
            >
              <i class="ri-more-2-fill text-lg" aria-hidden="true"></i>
            </button>

            <div
              v-if="detailActionsOpen"
              class="absolute right-0 top-[calc(100%+0.5rem)] z-30 grid min-w-[9.5rem] overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 shadow-xl shadow-slate-950/10"
            >
              <button class="detail-action-item" type="button" @click="openShare">
                <i class="ri-share-forward-line" aria-hidden="true"></i>
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="hasFlyerImages && !isBusinessPost" class="relative overflow-hidden bg-slate-950/30">
          <button
            class="absolute right-3 top-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-slate-950/45 text-lg text-white shadow-lg backdrop-blur transition hover:bg-blue-600"
            type="button"
            aria-label="Open images fullscreen"
            @click="openLightGallery(0)"
          >
            <i class="ri-expand-diagonal-line" aria-hidden="true"></i>
          </button>
          <FlyerImageCarousel :images="flyerImages" />
        </div>

        <section
          v-if="isBusinessPost"
          class="review-detail-summary"
          :class="{ 'review-detail-summary-no-image': isCheckin || !hasFlyerImages }"
        >
          <div v-if="businessMapEmbedUrl" class="review-map-embed">
            <iframe
              :src="businessMapEmbedUrl"
              :title="`${businessName} location map`"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              allowfullscreen
            ></iframe>
          </div>

          <div class="review-business-row">
            <span class="review-business-avatar" aria-hidden="true">{{ businessInitial }}</span>
            <span class="min-w-0 flex-1">
              <span class="review-type-label">{{ isCheckin ? 'Business checkin' : 'Business review' }}</span>
              <h1 class="review-business-name">{{ businessName }}</h1>
              <span class="review-business-address">
                <i class="ri-map-pin-2-line" aria-hidden="true"></i>
                <span>{{ businessAddress || 'Address not available' }}</span>
              </span>
            </span>
          </div>

          <div v-if="isReview" class="review-rating-row" :aria-label="`${reviewRating} out of 5 stars`">
            <span class="review-rating-stars">
              <svg
                v-for="star in 5"
                :key="star"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
                :class="{ 'review-star-active': star <= reviewRating }"
                aria-hidden="true"
              >
                <path d="m12 2.8 2.85 5.78 6.38.93-4.62 4.5 1.09 6.35L12 17.37l-5.7 3 1.09-6.35-4.62-4.5 6.38-.93L12 2.8Z" />
              </svg>
            </span>
            <strong>{{ reviewRating }}/5</strong>
          </div>

          <blockquote
            class="review-comment"
            v-html="reviewCommentHtml"
          ></blockquote>

          <Swiper
            v-if="isReview && flyerImages.length"
            class="review-detail-image-strip"
            :modules="reviewSwiperModules"
            :slides-per-view="3"
            :slides-per-group="1"
            :space-between="10"
            :watch-overflow="true"
            :pagination="{ clickable: true, dynamicBullets: true }"
            aria-label="Review photos"
          >
            <SwiperSlide
              v-for="(image, imageIndex) in flyerImages"
              :key="`${image}-${imageIndex}`"
            >
              <button
                class="review-detail-image-tile"
                type="button"
                :aria-label="`Open review photo ${imageIndex + 1}`"
                @click="openLightGallery(imageIndex)"
              >
                <img
                  :src="image"
                  :alt="`${businessName} review photo ${imageIndex + 1}`"
                  loading="lazy"
                  decoding="async"
                />
                <span class="review-detail-image-expand" aria-hidden="true">
                  <i class="ri-expand-diagonal-line"></i>
                </span>
              </button>
            </SwiperSlide>
          </Swiper>

          <a
            v-if="businessMapUrl"
            class="review-map-link"
            :href="businessMapUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="ri-map-2-line" aria-hidden="true"></i>
            <span>View business location</span>
            <i class="ri-arrow-right-up-line" aria-hidden="true"></i>
          </a>

          <router-link
            v-if="isReview && businessId"
            class="review-map-link justify-self-center"
            :to="{ name: 'business-reviews', params: { businessId }, query: { name: businessName } }"
          >
            <i class="ri-chat-quote-line" aria-hidden="true"></i>
            <span>View more reviews</span>
            <i class="ri-arrow-right-line" aria-hidden="true"></i>
          </router-link>
        </section>

        <div class="border-t border-slate-100 px-4 py-3 sm:px-5">
          <router-link
            v-if="isBusinessPost"
            class="mb-3 flex min-w-0 items-center gap-3 border-b border-slate-100 pb-3 transition hover:text-blue-700"
            :to="postedByRoute"
          >
            <img
              v-if="flyer.authorAvatar"
              class="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
              :src="flyer.authorAvatar"
              :alt="flyer.author || 'Author'"
              loading="lazy"
            />
            <div v-else class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
              {{ authorInitials }}
            </div>
            <span class="min-w-0 flex-1">
              <strong class="block truncate text-sm font-semibold text-slate-950">{{ flyer.author || 'Unknown author' }}</strong>
              <span class="block truncate text-xs text-slate-500">
                <span v-if="postedByUsername">{{ postedByUsername }}</span>
                <span v-if="postedByUsername && formattedDate"> · </span>
                <span>{{ formattedDate }}</span>
              </span>
            </span>
            <i class="ri-arrow-right-s-line shrink-0 text-lg text-slate-400" aria-hidden="true"></i>
          </router-link>

          <div v-if="!isBusinessPost && (flyerTitle || flyerStartDateText || flyerEndDateText || descriptionText)" class="grid gap-3">
            <h1 v-if="flyerTitle" class="m-0 text-xl font-semibold leading-7 text-slate-950 sm:text-2xl sm:leading-8">
              {{ flyerTitle }}
            </h1>
            <div
              v-if="flyerStartDateText || flyerEndDateText"
              class="flex w-fit max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 ring-1 ring-blue-100"
            >
              <i class="ri-calendar-event-line shrink-0 text-base text-blue-600" aria-hidden="true"></i>
              <span v-if="flyerStartDateText" class="whitespace-nowrap">
                <span class="text-blue-500">From</span> {{ flyerStartDateText }}
              </span>
              <i v-if="flyerStartDateText && flyerEndDateText" class="ri-arrow-right-line shrink-0 text-blue-400" aria-hidden="true"></i>
              <span v-if="flyerEndDateText" class="whitespace-nowrap">
                <span class="text-blue-500">To</span> {{ flyerEndDateText }}
              </span>
            </div>
            <div
              v-if="visibleDescriptionText"
              class="flyer-description text-sm leading-7 text-slate-700 sm:text-base"
              v-html="visibleDescriptionHtml"
            ></div>
            <div v-if="visibleDescriptionTags.length" class="flex flex-wrap gap-2">
              <router-link
                v-for="tag in visibleDescriptionTags"
                :key="tag"
                class="inline-flex max-w-full items-center rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold leading-6 text-blue-700 ring-1 ring-blue-100 transition hover:bg-blue-100 hover:text-blue-800"
                :to="{ name: 'hashtag-flyers', params: { hashtag: normalizeHashtagRouteParam(tag) } }"
              >
                {{ tag }}
              </router-link>
            </div>
            <button
              v-if="canToggleDescription"
              type="button"
              class="inline-flex w-fit items-center text-sm font-semibold text-blue-700 transition hover:text-blue-800"
              :aria-expanded="descriptionExpanded"
              @click="descriptionExpanded = !descriptionExpanded"
            >
              {{ descriptionExpanded ? 'Show less' : 'Show more' }}
            </button>
          </div>

          <div
            v-if="!isCheckin || canManageFlyer"
            class="flex items-center gap-1.5 sm:gap-2"
            :class="{ 'mt-4 border-t border-slate-100 pt-3': !isBusinessPost && (flyerTitle || flyerStartDateText || flyerEndDateText || descriptionText) }"
          >
            <button
              type="button"
              class="action-tooltip detail-social-action text-slate-900 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:text-slate-900"
              :disabled="likeSaving || isOwnFlyer"
              :aria-label="isOwnFlyer ? 'Like unavailable for your flyer' : likeCountLabel"
              :data-tooltip="isOwnFlyer ? 'Like unavailable' : likeCountLabel"
              @mouseenter="showActionTooltip($event, isOwnFlyer ? 'Like unavailable' : likeCountLabel)"
              @mouseleave="hideActionTooltip"
              @focus="showActionTooltip($event, isOwnFlyer ? 'Like unavailable' : likeCountLabel)"
              @blur="hideActionTooltip"
              @click="toggleLike"
            >
              <i class="text-base" :class="liked ? 'ri-heart-fill text-rose-500' : 'ri-heart-line text-slate-500'" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="action-tooltip detail-social-action text-slate-900 hover:text-blue-700"
              :aria-label="`${commentsCount} Comments`"
              :data-tooltip="`${commentsCount} Comments`"
              @mouseenter="showActionTooltip($event, `${commentsCount} Comments`)"
              @mouseleave="hideActionTooltip"
              @focus="showActionTooltip($event, `${commentsCount} Comments`)"
              @blur="hideActionTooltip"
              @click="focusCommentBox"
            >
              <i class="ri-chat-3-line text-base text-slate-500" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="action-tooltip detail-social-action text-slate-900 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:text-slate-900"
              :disabled="isOwnFlyer"
              :aria-label="isOwnFlyer ? 'Chat unavailable for your post' : 'Chat'"
              :data-tooltip="isOwnFlyer ? 'Chat unavailable' : 'Chat'"
              @mouseenter="showActionTooltip($event, isOwnFlyer ? 'Chat unavailable' : 'Chat')"
              @mouseleave="hideActionTooltip"
              @focus="showActionTooltip($event, isOwnFlyer ? 'Chat unavailable' : 'Chat')"
              @blur="hideActionTooltip"
              @click="openChat"
            >
              <i class="ri-message-3-line text-base text-slate-500" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="action-tooltip detail-social-action ml-auto text-slate-900 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:text-slate-900"
              :disabled="isOwnFlyer"
              :aria-label="isOwnFlyer ? 'Save unavailable for your post' : (saved ? 'Saved' : 'Save')"
              :data-tooltip="isOwnFlyer ? 'Save unavailable' : (saved ? 'Saved' : 'Save')"
              @mouseenter="showActionTooltip($event, isOwnFlyer ? 'Save unavailable' : (saved ? 'Saved' : 'Save'))"
              @mouseleave="hideActionTooltip"
              @focus="showActionTooltip($event, isOwnFlyer ? 'Save unavailable' : (saved ? 'Saved' : 'Save'))"
              @blur="hideActionTooltip"
              @click="openSaveBoard"
            >
              <i class="text-base" :class="saved ? 'ri-bookmark-fill text-blue-600' : 'ri-bookmark-line text-slate-500'" aria-hidden="true"></i>
            </button>
            <router-link
              v-if="canManageFlyer"
              :to="editFlyerRoute"
              class="action-tooltip detail-social-action text-slate-900 hover:text-blue-700"
              :aria-label="`Edit ${contentTypeName}`"
              :data-tooltip="`Edit ${contentTypeName}`"
              @mouseenter="showActionTooltip($event, `Edit ${contentTypeName}`)"
              @mouseleave="hideActionTooltip"
              @focus="showActionTooltip($event, `Edit ${contentTypeName}`)"
              @blur="hideActionTooltip"
            >
              <i class="ri-edit-2-line text-base text-slate-500" aria-hidden="true"></i>
            </router-link>
            <button
              v-if="canManageFlyer"
              type="button"
              class="action-tooltip detail-social-action bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="deletingFlyer"
              :aria-label="deletingFlyer ? `Deleting ${contentTypeName}` : `Delete ${contentTypeName}`"
              :data-tooltip="deletingFlyer ? 'Deleting...' : `Delete ${contentTypeName}`"
              @mouseenter="showActionTooltip($event, deletingFlyer ? 'Deleting...' : `Delete ${contentTypeName}`)"
              @mouseleave="hideActionTooltip"
              @focus="showActionTooltip($event, deletingFlyer ? 'Deleting...' : `Delete ${contentTypeName}`)"
              @blur="hideActionTooltip"
              @click="deleteCurrentFlyer"
            >
              <i class="ri-delete-bin-6-line text-base" aria-hidden="true"></i>
            </button>
          </div>

        </div>
      </div>

      <aside class="w-full max-w-[360px] justify-self-center flex flex-col gap-4 self-start sm:sticky sm:top-24 xl:justify-self-auto">
        <section v-if="ownerActionError || canShowPublishControl" class="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <p v-if="ownerActionError" class="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
            {{ ownerActionError }}
          </p>

          <div v-if="canShowPublishControl" class="grid gap-3" :class="{ 'mt-3': ownerActionError }">
            <button
              type="button"
              class="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-200/70 transition hover:brightness-105"
              :disabled="publishingFlyer"
              @click="togglePublishStatus"
            >
              <i :class="statusLabel === 'Active' ? 'ri-eye-off-line' : 'ri-upload-cloud-2-line'" aria-hidden="true"></i>
              <span>{{ publishingFlyer ? 'Updating...' : statusLabel === 'Active' ? 'Unpublish' : 'Publish' }}</span>
            </button>
          </div>
        </section>

        <section class="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-2xl font-semibold text-slate-750">Comments</h2>
            <span class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {{ commentsCount }}
            </span>
          </div>

          <form class="mt-4 grid gap-3" @submit.prevent="addComment">
            <textarea
              ref="commentBox"
              v-model="draft"
              rows="4"
              class="min-h-[132px] rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100"
              placeholder="Enter comment..."
            />
            <p v-if="commentError" class="m-0 rounded-2xl bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
              {{ commentError }}
            </p>
            <div class="flex items-center justify-between gap-3">
              <span class="text-sm text-slate-400">Be specific and keep it useful.</span>
              <button
                type="submit"
                class="nf-primary-button"
                :disabled="commentSubmitting || !draft.trim()"
              >
                {{ commentSubmitting ? 'Posting...' : 'Comment' }}
              </button>
            </div>
          </form>

          <div class="mt-5 border-t border-slate-200 pt-5">
            <p v-if="commentsLoading" class="text-sm font-medium text-slate-400">Loading comments...</p>
            <div v-else-if="comments.length" class="relative">
              <div class="grid gap-3">
              <article
                v-for="comment in visibleComments"
                :key="comment.id"
                class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
              >
                <div class="flex items-start gap-3">
                    <img
                      v-if="comment.avatar"
                      class="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
                      :src="comment.avatar"
                      :alt="comment.author"
                      loading="lazy"
                    />
                    <div v-else class="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-900 text-[11px] font-semibold text-white ring-1 ring-slate-200">
                      {{ commentInitials(comment) }}
                    </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-start justify-between gap-3">
                      <div class="min-w-0">
                        <strong class="block truncate text-xs font-semibold text-slate-950">{{ comment.author }}</strong>
                        <span v-if="comment.username" class="block truncate text-[11px] font-semibold text-slate-400">{{ comment.username }}</span>
                      </div>
                      <span class="shrink-0 text-[11px] font-medium text-slate-400">{{ comment.time }}</span>
                    </div>
                    <p class="pt-1.5 text-sm leading-6 text-slate-600">
                      <span>{{ visibleCommentBody(comment) }}</span>
                      <button
                        v-if="canToggleCommentBody(comment)"
                        type="button"
                        class="ml-1 inline-flex text-xs font-semibold text-blue-700 transition hover:text-blue-800"
                        @click="toggleCommentBody(comment.id)"
                      >
                        {{ isCommentBodyExpanded(comment.id) ? 'Less' : 'Show more' }}
                      </button>
                    </p>
                    <button
                      type="button"
                      class="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 transition hover:text-blue-800"
                      @click="startReply(comment)"
                    >
                      <i class="ri-reply-line" aria-hidden="true"></i>
                      <span>Reply</span>
                    </button>
                  </div>
                </div>

                <form v-if="replyingToId === comment.id" class="mt-3 grid gap-2 pl-11" @submit.prevent="addReply(comment)">
                  <textarea
                    v-model="replyDraft"
                    rows="2"
                    class="min-h-[72px] rounded-2xl border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                    :placeholder="`Reply to ${comment.author}`"
                  />
                  <div class="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      class="inline-flex min-h-9 items-center justify-center rounded-xl bg-slate-100 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
                      :disabled="replySubmitting"
                      @click="cancelReply"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      class="nf-primary-button nf-primary-button--compact"
                      :disabled="replySubmitting || !replyDraft.trim()"
                    >
                      {{ replySubmitting ? 'Replying...' : 'Reply' }}
                    </button>
                  </div>
                </form>

                <button
                  v-if="comment.replies.length"
                  type="button"
                  class="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 ring-1 ring-blue-100 transition hover:bg-blue-50 hover:text-blue-800"
                  @click="toggleReplies(comment.id)"
                >
                  <i :class="isRepliesExpanded(comment.id) ? 'ri-arrow-up-s-line' : 'ri-chat-3-line'" aria-hidden="true"></i>
                  <span>{{ repliesToggleLabel(comment) }}</span>
                </button>

                <div v-if="comment.replies.length && isRepliesExpanded(comment.id)" class="mt-3 grid gap-2 border-l-2 border-slate-200 pl-4">
                  <article
                    v-for="reply in comment.replies"
                    :key="reply.id"
                    class="rounded-2xl bg-white px-3 py-2 ring-1 ring-slate-200"
                  >
                    <div class="flex items-start gap-2">
                      <img
                        v-if="reply.avatar"
                        class="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
                        :src="reply.avatar"
                        :alt="reply.author"
                        loading="lazy"
                      />
                      <div v-else class="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-900 text-[11px] font-semibold text-white ring-1 ring-slate-200">
                        {{ commentInitials(reply) }}
                      </div>
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center justify-between gap-2">
                          <span class="min-w-0">
                            <strong class="block truncate text-xs font-semibold text-slate-950">{{ reply.author }}</strong>
                            <span v-if="reply.username" class="block truncate text-[11px] font-semibold text-slate-400">{{ reply.username }}</span>
                          </span>
                          <span class="shrink-0 text-[11px] font-medium text-slate-400">{{ reply.time }}</span>
                        </div>
                        <p class="pt-1 text-sm leading-6 text-slate-600">
                          <span>{{ visibleCommentBody(reply) }}</span>
                          <button
                            v-if="canToggleCommentBody(reply)"
                            type="button"
                            class="ml-1 inline-flex text-xs font-semibold text-blue-700 transition hover:text-blue-800"
                            @click="toggleCommentBody(reply.id)"
                          >
                            {{ isCommentBodyExpanded(reply.id) ? 'Less' : 'Show more' }}
                          </button>
                        </p>
                      </div>
                    </div>
                  </article>
                </div>
              </article>
              </div>

              <div
                v-if="hasHiddenComments"
                class="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center rounded-b-2xl bg-gradient-to-t from-white via-white/95 to-white/0 px-4 pb-2 pt-12"
              >
                <button
                  type="button"
                  class="nf-primary-button pointer-events-auto shadow-lg shadow-blue-900/15"
                  @click="commentsExpanded = true"
                >
                  <i class="ri-chat-3-line" aria-hidden="true"></i>
                  <span>Show {{ hiddenCommentsCount }} more {{ hiddenCommentsCount === 1 ? 'comment' : 'comments' }}</span>
                </button>
              </div>
            </div>
            <p v-else class="text-sm text-slate-400">No comments yet.</p>
          </div>
        </section>
      </aside>
    </div>

    <Teleport to="body">
      <ShareFlyerModal v-if="shareModalOpen" :flyer="flyer" @close="shareModalOpen = false" />
      <div
        v-if="actionTooltip.visible"
        class="fixed z-[90] -translate-x-1/2 rounded-md bg-slate-950 px-2.5 py-1.5 text-center text-[0.7rem] font-medium leading-none text-white shadow-lg shadow-slate-950/20"
        :style="{ left: `${actionTooltip.left}px`, top: `${actionTooltip.top}px` }"
        role="tooltip"
      >
        {{ actionTooltip.label }}
      </div>
      <SaveToBoardModal
        v-if="saveBoardModalOpen"
        :flyer-id="flyer?.id || route.params.id"
        :initial-saved="saved"
        :initial-board-id="activeSavedBoardId"
        @close="saveBoardModalOpen = false"
        @saved-change="handleSavedChange"
      />
    </Teleport>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import FlyerImageCarousel from '@/components/common/FlyerImageCarousel.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { extractFlyerDetail, normalizeFlyerDetail } from '@/utils/flyerDetail'
import { formatDisplayDate, formatRelativeTime } from '@/utils/date'
import ShareFlyerModal from '@/components/common/ShareFlyerModal.vue'
import SaveToBoardModal from '@/components/common/SaveToBoardModal.vue'
import lightGallery from 'lightgallery'
import lgZoom from 'lightgallery/plugins/zoom'
import 'lightgallery/css/lightgallery.css'
import 'lightgallery/css/lg-zoom.css'
import 'swiper/css'
import 'swiper/css/pagination'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const commentBox = ref(null)
const draft = ref('')
const liked = ref(false)
const saved = ref(false)
const descriptionExpanded = ref(false)
const localLikesCount = ref(0)
const likeSaving = ref(false)
const saveBoardModalOpen = ref(false)
const savedBoardIds = ref(new Set())
const isLoading = ref(false)
const apiFlyer = ref(null)
const comments = ref([])
const commentsLoading = ref(false)
const commentSubmitting = ref(false)
const commentError = ref('')
const replyingToId = ref('')
const replyDraft = ref('')
const replySubmitting = ref(false)
const expandedReplyIds = ref(new Set())
const expandedCommentBodyIds = ref(new Set())
const commentsExpanded = ref(false)
const deletingFlyer = ref(false)
const publishingFlyer = ref(false)
const ownerActionError = ref('')
const detailActionsOpen = ref(false)
const shareModalOpen = ref(false)
const actionTooltip = reactive({
  visible: false,
  label: '',
  left: 0,
  top: 0
})
const lightGalleryHost = ref(null)
let lightGalleryInstance = null
const COMMENTS_PREVIEW_LIMIT = 3
const COMMENT_BODY_PREVIEW_LIMIT = 30
const DESCRIPTION_PREVIEW_LIMIT = 180
const reviewSwiperModules = [Pagination]

const fallbackFlyer = computed(() => ({
  id: route.params.id || '',
  flyer_id: route.params.id || '',
  author: '',
  authorAvatar: '',
  description: '',
  images: [],
  image: '',
  likesCount: 0,
  saved: false,
  liked: false,
  flyer_type: String(route.query.type || '').toLowerCase()
}))
const flyer = computed(() => apiFlyer.value || fallbackFlyer.value)
const flyerType = computed(() => String(route.query.type || flyer.value?.flyer_type || flyer.value?.flyerType || '').toLowerCase())
const isCheckin = computed(() => flyerType.value === 'checkin')
const isReview = computed(() => (
  flyerType.value === 'review' ||
  (!flyerType.value && Boolean(flyer.value?.business_name || flyer.value?.businessName || flyer.value?.s_business || flyer.value?.review_comment || flyer.value?.review))
))
const isBusinessPost = computed(() => isReview.value || isCheckin.value)
const contentTypeName = computed(() => isCheckin.value ? 'checkin' : isReview.value ? 'review' : 'flyer')
const businessName = computed(() => String(flyer.value?.business_name || flyer.value?.businessName || flyer.value?.s_business || flyer.value?.title || 'Business').trim())
const businessId = computed(() => {
  const id = String(flyer.value?.business_id || flyer.value?.businessId || '').trim()
  return id && id !== '0' ? id : ''
})
const businessAddress = computed(() => String(
  flyer.value?.business_adress ||
  flyer.value?.business_address ||
  flyer.value?.businessAddress ||
  flyer.value?.s_address ||
  flyer.value?.location ||
  flyer.value?.address ||
  ''
).trim())
const businessInitial = computed(() => businessName.value.charAt(0).toUpperCase() || 'B')
const reviewRating = computed(() => {
  const rating = Number(flyer.value?.review_count || flyer.value?.rating || 0)
  if (!Number.isFinite(rating)) return 0
  return Math.min(5, Math.max(0, rating))
})
const businessLatitude = computed(() => String(
  flyer.value?.business_lat ?? flyer.value?.businessLatitude ?? flyer.value?.business_latitude ?? flyer.value?.s_latit ?? ''
).trim())
const businessLongitude = computed(() => String(
  flyer.value?.business_long ?? flyer.value?.businessLong ?? flyer.value?.business_longitude ?? flyer.value?.s_longit ?? ''
).trim())
const hasBusinessCoordinates = computed(() => {
  if (!businessLatitude.value || !businessLongitude.value) return false
  const latitude = Number(businessLatitude.value)
  const longitude = Number(businessLongitude.value)
  return Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0) &&
    latitude >= -90 && latitude <= 90 &&
    longitude >= -180 && longitude <= 180
})
const businessMapEmbedUrl = computed(() => {
  if (!hasBusinessCoordinates.value) return ''
  const coordinates = encodeURIComponent(`${businessLatitude.value},${businessLongitude.value}`)
  return `https://www.google.com/maps?q=${coordinates}&z=15&output=embed`
})
const businessMapUrl = computed(() => {
  const query = hasBusinessCoordinates.value
    ? `${businessLatitude.value},${businessLongitude.value}`
    : businessAddress.value
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : ''
})
const activeSavedBoardId = computed(() => Array.from(savedBoardIds.value)[0] || flyer.value?.boardId || flyer.value?.board_id || '')

const actualFlyerImages = computed(() => {
  const images = [...(Array.isArray(flyer.value?.images) ? flyer.value.images : []), flyer.value?.image].filter(Boolean)
  return Array.from(new Set(images))
})

const hasFlyerImages = computed(() => actualFlyerImages.value.length > 0)

const flyerImages = computed(() => {
  return actualFlyerImages.value
})

const stripDescriptionHashtags = (value) =>
  String(value || '')
    .replace(/(^|\s)#[\p{L}\p{N}_-]+/gu, (match, prefix = '') => (prefix.includes('\n') ? '\n' : ' '))
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

const decodeHtmlEntities = (value) => {
  let decoded = String(value || '')
  if (!decoded) return ''

  for (let index = 0; index < 5; index += 1) {
    const previous = decoded
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = decoded
      decoded = textarea.value
    } else {
      decoded = decoded
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
    }
    if (decoded === previous) break
  }

  return decoded
}

const hasHtmlMarkup = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ''))

const stripHtmlTags = (value) => {
  const source = String(value || '')
  if (!hasHtmlMarkup(source)) return source
  if (typeof document !== 'undefined') {
    const template = document.createElement('template')
    template.innerHTML = source
    return template.content.textContent || ''
  }
  return source.replace(/<[^>]*>/g, ' ')
}

const sanitizeDescriptionHtml = (value) => {
  const source = String(value || '')
  if (!hasHtmlMarkup(source)) return linkifyDescription(source)
  if (typeof document === 'undefined') return linkifyDescription(stripHtmlTags(source))

  const allowedTags = new Set(['P', 'DIV', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'A'])
  const template = document.createElement('template')
  template.innerHTML = source

  template.content.querySelectorAll('*').forEach((node) => {
    if (!allowedTags.has(node.tagName)) {
      if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED'].includes(node.tagName)) {
        node.remove()
        return
      }
      node.replaceWith(document.createTextNode(node.textContent || ''))
      return
    }

    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase()
      const value = attribute.value || ''
      if (node.tagName === 'A' && name === 'href' && /^(https?:|mailto:|tel:)/i.test(value)) return
      if (node.tagName === 'A' && ['target', 'rel'].includes(name)) return
      node.removeAttribute(attribute.name)
    })

    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })

  return template.innerHTML
}

const flyerTitle = computed(() => decodeHtmlEntities(flyer.value?.title || '').trim())
const flyerStartDateText = computed(() => formatDisplayDate(flyer.value?.startsDate || flyer.value?.start_date || ''))
const flyerEndDateText = computed(() => formatDisplayDate(flyer.value?.endsDate || flyer.value?.end_date || ''))
const descriptionText = computed(() => decodeHtmlEntities(flyer.value?.description || '').trim())
const reviewCommentSource = computed(() => decodeHtmlEntities(
  flyer.value?.review_comment || flyer.value?.comments || flyer.value?.review || flyer.value?.description || ''
).trim())
const reviewCommentHtml = computed(() => sanitizeDescriptionHtml(
  reviewCommentSource.value || (isCheckin.value ? 'No checkin comment provided.' : 'No review comment provided.')
))
const descriptionBodyText = computed(() => stripDescriptionHashtags(descriptionText.value))
const descriptionPlainText = computed(() => stripDescriptionHashtags(stripHtmlTags(descriptionText.value)).trim())
const canToggleDescription = computed(() => descriptionPlainText.value.length > DESCRIPTION_PREVIEW_LIMIT)
const visibleDescriptionText = computed(() => {
  if (!canToggleDescription.value || descriptionExpanded.value) return descriptionBodyText.value
  return `${descriptionPlainText.value.slice(0, DESCRIPTION_PREVIEW_LIMIT).trim()}...`
})

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

const trimLinkedText = (value) => {
  const text = String(value || '')
  const leading = text.match(/^[([{<]+/)?.[0] || ''
  const trailing = text.match(/[)\]}>.,!?:;]+$/)?.[0] || ''
  const core = text.slice(leading.length, trailing ? -trailing.length : undefined)
  return {
    leading,
    linkText: core,
    trailing
  }
}

const getDescriptionLinkHref = (value) => {
  const text = String(value || '')
  if (/^https?:\/\//i.test(text)) return text
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(text)) return `mailto:${text}`
  if (/^(www\.|[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\/[^\s<>"']*)?$)/i.test(text)) return `https://${text}`
  if (/^\+?\d[\d\s().-]{7,}\d$/.test(text)) return `tel:${text.replace(/[^\d+]/g, '')}`
  return ''
}

const linkifyDescription = (value) => {
  const source = String(value || '')
  return source
    .split(/(\s+)/)
    .map((token) => {
      if (!token || /^\s+$/.test(token)) return escapeHtml(token).replace(/\n/g, '<br>')

      const { leading, linkText, trailing } = trimLinkedText(token)
      const href = getDescriptionLinkHref(linkText)
      if (!href) return escapeHtml(token).replace(/\n/g, '<br>')

      return `${escapeHtml(leading)}<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(linkText)}</a>${escapeHtml(trailing)}`
    })
    .join('')
}

const visibleDescriptionHtml = computed(() => {
  if (!canToggleDescription.value || descriptionExpanded.value) {
    return sanitizeDescriptionHtml(visibleDescriptionText.value)
  }
  return linkifyDescription(visibleDescriptionText.value)
})
const visibleDescriptionTags = computed(() => {
  const tags = descriptionText.value.match(/(^|\s)(#[\p{L}\p{N}_-]+)/gu) || []
  return Array.from(new Set(tags.map((tag) => tag.trim().replace(/^[^#]*/, '')).filter(Boolean)))
})

const normalizeHashtagRouteParam = (tag) => String(tag || '').replace(/^#/, '').trim()

const openLightGallery = (imageIndex = 0) => {
  if (!hasFlyerImages.value) return

  if (!lightGalleryHost.value) {
    lightGalleryHost.value = document.createElement('div')
    document.body.appendChild(lightGalleryHost.value)
  }

  lightGalleryInstance?.destroy()
  lightGalleryInstance = lightGallery(lightGalleryHost.value, {
    dynamic: true,
    dynamicEl: flyerImages.value.map((image, index) => ({
      src: image,
      thumb: image,
      subHtml: flyer.value?.title || `Flyer image ${index + 1}`
    })),
    plugins: [lgZoom],
    download: false,
    counter: flyerImages.value.length > 1
  })

  const initialIndex = Math.min(Math.max(Number(imageIndex) || 0, 0), flyerImages.value.length - 1)
  lightGalleryInstance.openGallery(initialIndex)
}

const formattedDate = computed(() => {
  return String(flyer.value?.time_ago || flyer.value?.time_Ago || flyer.value?.timeAgo || '').trim()
})

const statusLabel = computed(() => {
  const status = String(flyer.value?.status || '').toLowerCase()
  return ['1', 'active', 'published', 'true'].includes(status) ? 'Active' : 'Inactive'
})

const authorInitials = computed(() => {
  const name = flyer.value?.author?.trim() || 'NF'
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('')
})

const likesCount = computed(() => Math.max(0, Number(localLikesCount.value || flyer.value?.likesCount || 0)))
const likeCountLabel = computed(() => {
  if (likesCount.value > 0) return `${likesCount.value} ${likesCount.value === 1 ? 'Like' : 'Likes'}`
  return liked.value ? 'Liked' : 'Like'
})

const commentsCount = computed(() => {
  return comments.value.length
})
const visibleComments = computed(() => {
  if (commentsExpanded.value) return comments.value
  return comments.value.slice(0, COMMENTS_PREVIEW_LIMIT)
})
const hiddenCommentsCount = computed(() => Math.max(0, comments.value.length - COMMENTS_PREVIEW_LIMIT))
const hasHiddenComments = computed(() => !commentsExpanded.value && hiddenCommentsCount.value > 0)

const commentBodyText = (comment) => String(comment?.body || '').trim()

const canToggleCommentBody = (comment) => commentBodyText(comment).length > COMMENT_BODY_PREVIEW_LIMIT

const isCommentBodyExpanded = (commentId) => expandedCommentBodyIds.value.has(String(commentId))

const visibleCommentBody = (comment) => {
  const body = commentBodyText(comment)
  if (!canToggleCommentBody(comment) || isCommentBodyExpanded(comment?.id)) return body
  return `${body.slice(0, COMMENT_BODY_PREVIEW_LIMIT).trim()}...`
}

const toggleCommentBody = (commentId) => {
  const nextIds = new Set(expandedCommentBodyIds.value)
  const id = String(commentId)

  if (nextIds.has(id)) {
    nextIds.delete(id)
  } else {
    nextIds.add(id)
  }

  expandedCommentBodyIds.value = nextIds
}

const isRepliesExpanded = (commentId) => expandedReplyIds.value.has(String(commentId))

const setRepliesExpanded = (commentId, expanded) => {
  const nextIds = new Set(expandedReplyIds.value)
  const id = String(commentId)

  if (expanded) {
    nextIds.add(id)
  } else {
    nextIds.delete(id)
  }

  expandedReplyIds.value = nextIds
}

const toggleReplies = (commentId) => {
  setRepliesExpanded(commentId, !isRepliesExpanded(commentId))
}

const repliesToggleLabel = (comment) => {
  const count = comment?.replies?.length || 0
  if (isRepliesExpanded(comment?.id)) return 'Hide replies'
  return `${count} more ${count === 1 ? 'reply' : 'replies'}`
}

const postedByUserId = computed(() =>
  flyer.value?.userId ||
  flyer.value?.user_id ||
  flyer.value?.userid ||
  flyer.value?.uid ||
  flyer.value?.created_by ||
  flyer.value?.created_by_id ||
  flyer.value?.owner_id ||
  flyer.value?.author_id ||
  ''
)
const isOwnFlyer = computed(() => {
  const userId = String(getCurrentUserId() || '').trim()
  const ownerId = String(postedByUserId.value || '').trim()
  return !!userId && !!ownerId && userId === ownerId
})
const canManageFlyer = computed(() => {
  if (isOwnFlyer.value) return true
  const source = String(route.query.source || '')
  return ['created', 'account'].includes(source) && !postedByUserId.value
})
const canShowPublishControl = computed(() => canManageFlyer.value && !isOwnFlyer.value)
const editFlyerRoute = computed(() => {
  const target = {
    path: `/flyers/${flyer.value?.id || route.params.id}/edit`,
    query: { returnTo: route.fullPath }
  }

  if (!isCheckin.value) return target
  return {
    ...target,
    query: { ...target.query, type: 'checkin' },
    state: {
      checkin: JSON.parse(JSON.stringify(flyer.value))
    }
  }
})
const postedByUsername = computed(() => {
  const username = flyer.value?.username || flyer.value?.user_username || ''
  return username ? `@${String(username).replace(/^@/, '')}` : ''
})
const postedByRoute = computed(() => {
  if (isOwnFlyer.value) return '/account'
  return `/users/${postedByUserId.value || flyer.value?.id || route.params.id}`
})

const goBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/explore')
}

const openChat = () => {
  if (!requireLogin()) return
  const toId = postedByUserId.value || ''
  const flyerId = route.params.id || flyer.value?.id || ''
  sessionStorage.setItem('netflyr.chatTarget', JSON.stringify({
    to_id: toId,
    flyer_id: flyerId,
    to_name: flyer.value?.author || flyer.value?.user_name || flyer.value?.username || '',
    to_image: flyer.value?.authorAvatar || flyer.value?.user_img || flyer.value?.profile_image || '',
    flyer_title: flyer.value?.title || '',
    flyer_description: flyer.value?.description || '',
    flyer_image: flyer.value?.image || flyerImages.value?.[0] || '',
    flyer_category: flyer.value?.category || flyer.value?.category_name || '',
    source: 'flyer',
    savedAt: Date.now()
  }))

  router.push('/chats')
}

const showActionTooltip = (event, label) => {
  const tooltipLabel = String(label || '').trim()
  const target = event?.currentTarget
  if (!tooltipLabel || !target?.getBoundingClientRect) return

  const rect = target.getBoundingClientRect()
  actionTooltip.label = tooltipLabel
  actionTooltip.left = Math.min(Math.max(rect.left + rect.width / 2, 48), window.innerWidth - 48)
  actionTooltip.top = rect.bottom + 8
  actionTooltip.visible = true
}

const hideActionTooltip = () => {
  actionTooltip.visible = false
}

const fetchFlyerDetail = async () => {
  const flyerId = route.params.id
  if (!flyerId) return

  isLoading.value = true
  apiFlyer.value = null

  try {
    const checkinState = String(route.query.type || '').toLowerCase() === 'checkin' && typeof window !== 'undefined'
      ? window.history.state?.checkin
      : null
    if (checkinState && typeof checkinState === 'object') {
      apiFlyer.value = normalizeFlyerDetail(
        { ...checkinState, flyer_type: 'checkin' },
        fallbackFlyer.value
      )
      return
    }

    const { data } = await api.detailFlyer({
      user_id: session.userId || session.user?.user_id || '',
      flyer_id: flyerId
    })
    const detail = extractFlyerDetail(data)
    const normalizedDetail = detail ? normalizeFlyerDetail(detail, fallbackFlyer.value) : fallbackFlyer.value
    apiFlyer.value = String(route.query.type || '').toLowerCase() === 'checkin'
      ? { ...normalizedDetail, flyer_type: 'checkin' }
      : normalizedDetail
  } catch (error) {
    apiFlyer.value = fallbackFlyer.value
  } finally {
    isLoading.value = false
  }
}

const extractComments = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.data,
    payload.list,
    payload.result,
    payload.comments,
    Array.isArray(payload.data) ? payload.data : payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.comments
  ]

  return candidates.find((candidate) => Array.isArray(candidate)) || []
}

const formatCommentTime = (value) => formatRelativeTime(value)

const commentAvatar = (item = {}) =>
  item.user_img ||
  item.author_img ||
  item.profile_image ||
  ''

const commentInitials = (comment = {}) => {
  const name = String(comment.author || comment.username || 'NF').trim()
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'NF'
}

const commentUsername = (item = {}) =>
  item.username ||
  item.user_username ||
  item.handle ||
  ''

const normalizeComment = (item = {}, index = 0) => {
  const replies = Array.isArray(item.replies) ? item.replies : []

  return {
    id: item.id || item.comment_id || item.flyer_comment_id || `comment-${index}`,
    author: item.user_name || item.author || item.username || item.name || 'NetFlyr user',
    username: commentUsername(item),
    avatar: commentAvatar(item),
    body: item.comment || item.body || item.comment_text || item.message || '',
    time: formatCommentTime(item.created_at || item.created_date || item.time || item.date),
    parentId: item.parent_id || item.parentId || '0',
    replies: replies.map((reply, replyIndex) => normalizeComment(reply, `${index}-${replyIndex}`))
  }
}

const fetchComments = async () => {
  const flyerId = route.params.id
  if (!flyerId) return

  commentsLoading.value = true
  commentError.value = ''

  try {
    const { data } = await api.viewCommentsFlyer({ flyer_id: flyerId })
    comments.value = extractComments(data).map(normalizeComment).filter((comment) => comment.body)
  } catch (error) {
    comments.value = []
    commentError.value = error?.response?.data?.message || 'Comments could not be loaded.'
  } finally {
    commentsLoading.value = false
  }
}

const focusCommentBox = () => {
  if (!requireLogin()) return
  commentBox.value?.focus()
}

const getCurrentUserId = () => session.userId || session.user?.user_id || session.user?.id || ''

const requireLogin = () => {
  if (getCurrentUserId()) return true
  router.push({ path: '/login', query: { redirect: route.fullPath } })
  return false
}

const toggleLike = async () => {
  const flyerId = flyer.value?.id || route.params.id
  const userId = getCurrentUserId()
  if (!flyerId || likeSaving.value || isOwnFlyer.value || !requireLogin()) return

  const previousLiked = liked.value
  const previousCount = likesCount.value
  liked.value = !liked.value
  localLikesCount.value = Math.max(0, previousCount + (liked.value ? 1 : -1))
  likeSaving.value = true

  try {
    await api.likeFlyer({ flyer_id: flyerId, user_id: userId })
  } catch (error) {
    liked.value = previousLiked
    localLikesCount.value = previousCount
  } finally {
    likeSaving.value = false
  }
}

const openSaveBoard = async () => {
  if (isOwnFlyer.value) return
  if (!requireLogin()) return
  saveBoardModalOpen.value = true
}

const handleSavedChange = ({ saved: nextSaved, boardIds = [] } = {}) => {
  saved.value = Boolean(nextSaved)
  savedBoardIds.value = new Set(boardIds.map((boardId) => String(boardId || '')).filter(Boolean))
}

const openShare = () => {
  detailActionsOpen.value = false
  shareModalOpen.value = true
}

const addComment = async () => {
  const body = draft.value.trim()
  if (!body || commentSubmitting.value) return
  if (!requireLogin()) return

  commentSubmitting.value = true
  commentError.value = ''

  try {
    await api.addCommentFlyer({
      flyer_id: route.params.id,
      user_id: session.userId || session.user?.user_id || '',
      comment: body,
      parent_id: '0'
    })

    draft.value = ''
    await fetchComments()
  } catch (error) {
    commentError.value = error?.response?.data?.message || 'Comment could not be posted. Please try again.'
  } finally {
    commentSubmitting.value = false
  }
}

const startReply = (comment) => {
  if (!requireLogin()) return

  replyingToId.value = comment?.id || ''
  replyDraft.value = ''
  commentError.value = ''
}

const cancelReply = () => {
  replyingToId.value = ''
  replyDraft.value = ''
}

const addReply = async (comment) => {
  const body = replyDraft.value.trim()
  const parentId = comment?.id || ''
  if (!body || !parentId || replySubmitting.value) return
  if (!requireLogin()) return

  replySubmitting.value = true
  commentError.value = ''

  try {
    await api.addCommentFlyer({
      flyer_id: route.params.id,
      user_id: session.userId || session.user?.user_id || '',
      comment: body,
      parent_id: parentId
    })

    cancelReply()
    await fetchComments()
    setRepliesExpanded(parentId, true)
  } catch (error) {
    commentError.value = error?.response?.data?.message || 'Reply could not be posted. Please try again.'
  } finally {
    replySubmitting.value = false
  }
}

const loadFlyerDetail = () => {
  fetchFlyerDetail()
  fetchComments()
}

watch(flyer, (nextFlyer) => {
  liked.value = Boolean(nextFlyer?.liked)
  saved.value = Boolean(nextFlyer?.saved)
  localLikesCount.value = Number(nextFlyer?.likesCount || 0)
  const nextSavedBoardIds = new Set()
  const boardId = String(nextFlyer?.boardId || nextFlyer?.board_id || '').trim()
  if (saved.value && boardId) nextSavedBoardIds.add(boardId)
  savedBoardIds.value = nextSavedBoardIds
}, {
  immediate: true
})

const deleteCurrentFlyer = async () => {
  const flyerId = flyer.value?.id || route.params.id
  if (!flyerId || deletingFlyer.value) return

  const result = await Swal.fire({
    icon: 'warning',
    title: `Delete ${contentTypeName.value}?`,
    text: 'This action cannot be undone.',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#e11d48',
    cancelButtonColor: '#64748b'
  })

  if (!result.isConfirmed) return

  deletingFlyer.value = true
  ownerActionError.value = ''

  try {
    await api.deleteFlyer({
      flyer_id: flyerId,
      flyer_type: flyerType.value,
      user_id: getCurrentUserId(),
      business_id: flyer.value?.business_id || flyer.value?.businessId || '',
      checkin_date: flyer.value?.checkin_date || flyer.value?.postedAt || ''
    })
    await Swal.fire({
      icon: 'success',
      title: `${contentTypeName.value.charAt(0).toUpperCase()}${contentTypeName.value.slice(1)} deleted`,
      text: `The ${contentTypeName.value} has been removed.`,
      confirmButtonColor: '#2563eb'
    })
    router.push('/explore')
  } catch (error) {
    ownerActionError.value = error?.response?.data?.message || `${contentTypeName.value.charAt(0).toUpperCase()}${contentTypeName.value.slice(1)} could not be deleted. Please try again.`
    await Swal.fire({
      icon: 'error',
      title: 'Delete failed',
      text: ownerActionError.value,
      confirmButtonColor: '#e11d48'
    })
  } finally {
    deletingFlyer.value = false
  }
}

const togglePublishStatus = async () => {
  const flyerId = flyer.value?.id || route.params.id
  if (!flyerId || publishingFlyer.value) return

  publishingFlyer.value = true
  ownerActionError.value = ''

  try {
    await api.publishUnpublishFlyer({
      id: flyerId,
      status: statusLabel.value === 'Active' ? '0' : '1'
    })
    await fetchFlyerDetail()
  } catch (error) {
    ownerActionError.value = error?.response?.data?.message || 'Flyer status could not be updated. Please try again.'
  } finally {
    publishingFlyer.value = false
  }
}

watch(() => route.params.id, () => {
  comments.value = []
  draft.value = ''
  replyingToId.value = ''
  replyDraft.value = ''
  expandedReplyIds.value = new Set()
  expandedCommentBodyIds.value = new Set()
  commentsExpanded.value = false
  commentError.value = ''
  ownerActionError.value = ''
  detailActionsOpen.value = false
  shareModalOpen.value = false
  savedBoardIds.value = new Set()
  descriptionExpanded.value = false
  loadFlyerDetail()
}, {
  immediate: true
  })

onBeforeUnmount(() => {
  lightGalleryInstance?.destroy()
  lightGalleryInstance = null
  lightGalleryHost.value?.remove()
  lightGalleryHost.value = null
})
</script>

<style scoped>
.detail-social-action {
  align-items: center;
  background: transparent;
  border-radius: 999px;
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  min-width: 2.5rem;
  padding: 0;
  transition: color 0.16s ease, transform 0.16s ease;
}

.detail-social-action:hover {
  transform: translateY(-1px);
}

.detail-social-action i {
  color: currentColor;
  font-size: 1.45rem;
  line-height: 1;
}

.detail-action-item {
  display: flex;
  min-height: 2.4rem;
  align-items: center;
  gap: 0.6rem;
  padding: 0 0.9rem;
  text-align: left;
  font-size: 0.86rem;
  font-weight: 500;
  color: rgb(51 65 85);
  transition: background 0.16s ease, color 0.16s ease;
}

.detail-action-item i {
  font-size: 1rem;
  line-height: 1;
}

.detail-action-item:hover {
  background: rgb(248 250 252);
  color: rgb(15 23 42);
}

.flyer-description {
  overflow-wrap: anywhere;
}

.flyer-description :deep(a) {
  color: rgb(29 78 216);
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.18em;
}

.flyer-description :deep(a:hover) {
  color: rgb(30 64 175);
}

.flyer-description :deep(ul),
.flyer-description :deep(ol) {
  margin: 0.65rem 0;
  padding-left: 1.75rem;
  list-style-position: outside;
}

.flyer-description :deep(ul) {
  list-style-type: disc;
}

.flyer-description :deep(ol) {
  list-style-type: decimal;
}

.flyer-description :deep(li) {
  display: list-item;
  padding-left: 0.2rem;
}

.flyer-description :deep(li + li) {
  margin-top: 0.25rem;
}

.review-detail-card {
  border-color: rgb(226 232 240);
}

.review-detail-summary {
  display: grid;
  gap: 1rem;
  padding: 1.25rem;
  border-top: 1px solid rgb(241 245 249);
  background: white;
}

.review-detail-summary-no-image {
  padding-top: 1.5rem;
  background:
    radial-gradient(circle at 88% 8%, rgb(219 234 254 / 75%), transparent 32%),
    linear-gradient(145deg, rgb(255 251 235), white 52%);
}

.review-business-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.9rem;
}

.review-business-avatar {
  display: grid;
  height: 3.25rem;
  width: 3.25rem;
  flex-shrink: 0;
  place-items: center;
  border-radius: 1rem;
  background: linear-gradient(145deg, rgb(37 99 235), rgb(79 70 229));
  color: white;
  font-size: 1.05rem;
  font-weight: 700;
  box-shadow: 0 10px 24px rgb(37 99 235 / 20%);
}

.review-type-label {
  display: block;
  margin-bottom: 0.15rem;
  color: rgb(180 83 9);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.review-business-name {
  margin: 0;
  overflow: hidden;
  color: rgb(15 23 42);
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-business-address {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.25rem;
  color: rgb(100 116 139);
  font-size: 0.78rem;
}

.review-business-address i {
  flex-shrink: 0;
  color: rgb(37 99 235);
}

.review-business-address span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-rating-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.review-rating-row strong {
  color: rgb(71 85 105);
  font-size: 0.8rem;
}

.review-rating-stars {
  display: flex;
  gap: 0.22rem;
}

.review-rating-stars svg {
  height: 1.25rem;
  width: 1.25rem;
  fill: rgb(241 245 249);
  color: rgb(203 213 225);
}

.review-rating-stars .review-star-active {
  fill: rgb(251 191 36);
  color: rgb(245 158 11);
}

.review-comment {
  margin: 0;
  border-left: 3px solid rgb(251 191 36);
  border-radius: 0 0.9rem 0.9rem 0;
  background: rgb(248 250 252);
  padding: 0.9rem 1rem;
  color: rgb(51 65 85);
  font-size: 0.95rem;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.review-comment :deep(p),
.review-comment :deep(div) {
  margin: 0;
}

.review-comment :deep(p + p),
.review-comment :deep(div + div) {
  margin-top: 0.65rem;
}

.review-comment :deep(ul),
.review-comment :deep(ol) {
  margin: 0.65rem 0;
  padding-left: 1.5rem;
  list-style-position: outside;
}

.review-comment :deep(ul) {
  list-style-type: disc;
}

.review-comment :deep(ol) {
  list-style-type: decimal;
}

.review-comment :deep(li) {
  display: list-item;
  padding-left: 0.15rem;
}

.review-comment :deep(li + li) {
  margin-top: 0.25rem;
}

.review-comment :deep(a) {
  color: rgb(29 78 216);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.review-comment :deep(a:hover) {
  color: rgb(30 64 175);
}

.review-detail-image-strip {
  width: 100%;
  max-width: 100%;
  padding: 0.1rem 0 1.45rem;
}

.review-detail-image-strip :deep(.swiper-slide) {
  height: auto;
}

.review-detail-image-strip :deep(.swiper-pagination) {
  bottom: 0.15rem;
}

.review-detail-image-strip :deep(.swiper-pagination-bullet) {
  width: 0.42rem;
  height: 0.42rem;
  background: rgb(148 163 184);
  opacity: 0.65;
}

.review-detail-image-strip :deep(.swiper-pagination-bullet-active) {
  background: rgb(37 99 235);
  opacity: 1;
}

.review-detail-image-tile {
  position: relative;
  display: grid;
  width: 100%;
  height: 100%;
  min-height: 5.25rem;
  aspect-ratio: 4 / 3;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 0.9rem;
  background: rgb(248 250 252);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.review-detail-image-tile:hover {
  border-color: rgb(147 197 253);
  box-shadow: 0 8px 20px rgb(15 23 42 / 0.12);
  transform: translateY(-1px);
}

.review-detail-image-tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.review-detail-image-expand {
  position: absolute;
  right: 0.35rem;
  bottom: 0.35rem;
  display: grid;
  width: 1.55rem;
  height: 1.55rem;
  place-items: center;
  border-radius: 9999px;
  background: rgb(15 23 42 / 0.68);
  color: white;
  font-size: 0.78rem;
  backdrop-filter: blur(6px);
}

.review-map-link {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 0.45rem;
  border-radius: 9999px;
  background: rgb(239 246 255);
  padding: 0.55rem 0.85rem;
  color: rgb(29 78 216);
  font-size: 0.78rem;
  font-weight: 600;
  transition: background 0.16s ease, color 0.16s ease;
}

.review-map-embed {
  position: relative;
  width: 100%;
  height: clamp(8rem, 24vw, 10rem);
  overflow: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
  background: rgb(241 245 249);
}

.review-map-embed iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

.review-map-link:hover {
  background: rgb(219 234 254);
  color: rgb(30 64 175);
}

@media (max-width: 640px) {
  .review-detail-summary {
    gap: 0.85rem;
    padding: 1rem;
  }

  .review-detail-summary-no-image {
    padding-top: 1.2rem;
  }

  .review-business-avatar {
    height: 2.8rem;
    width: 2.8rem;
    border-radius: 0.85rem;
  }

  .review-business-name {
    font-size: 1.05rem;
  }

  .review-comment {
    padding: 0.75rem 0.85rem;
    font-size: 0.88rem;
    line-height: 1.6;
  }

}

</style>
